import { useLayoutEffect, useRef, useState } from 'react';
import { cn } from '@/shared/utils/cn';

interface ExpandableTextProps {
  text: string;
  className?: string;
  clampClass?: string;
}

/** Text block that clamps to `clampClass` and reveals a toggle only when it actually overflows. */
export const ExpandableText = ({ text, className, clampClass = 'line-clamp-2' }: ExpandableTextProps) => {
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);

  useLayoutEffect(() => {
    if (expanded) return;

    const el = textRef.current;
    if (!el) return;

    const checkOverflow = () => setIsOverflowing(el.scrollHeight > el.clientHeight);
    checkOverflow();

    if (typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(checkOverflow);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text, expanded]);

  return (
    <div>
      {/* Descriptions carry paragraph and list breaks as newlines (converted from the feed's HTML).
          Only honour them when expanded: in the clamped preview a blank paragraph gap would use
          up one of the few visible lines. */}
      <p ref={textRef} className={cn(expanded ? 'whitespace-pre-line' : clampClass, className)}>
        {text}
      </p>
      {isOverflowing && (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            setExpanded((prev) => !prev);
          }}
          aria-expanded={expanded}
          className="text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          {expanded ? 'Show less' : 'more'}
        </button>
      )}
    </div>
  );
};
